import type {PropsWithChildren} from 'react';
import React, {useEffect, useMemo} from 'react';
import type {LoadStatus} from 'api/types';
import {MasterLocale} from 'localization/types';
import type {ThesaurusId, ThesaurusesState} from 'services/thesaurus/types';
import {useLocalization} from 'localization';
import {useAppSelector} from 'store/hooks';
import {Scene} from 'components/scene';
import {useLoadThesaurus} from '../hooks/useLoadThesaurus';
import {selectThesaurus} from '../thesaurusSlice';

type Props = {
  thesauruses: ThesaurusId[] | undefined;
};

const getAggregateStatus = (
  thesauruses: ThesaurusId[],
  state: ThesaurusesState,
): LoadStatus => {
  return thesauruses.reduce<LoadStatus>((acc, name) => {
    const thesaurusStatus = state[name]?.status ?? 'NotLoaded';
    return acc === 'Failed' || thesaurusStatus === 'Failed'
      ? 'Failed'
      : acc === 'Loaded' && thesaurusStatus === 'Loaded'
        ? 'Loaded'
        : 'Loading';
  }, 'Loaded');
};

const getNotLoaded = (
  thesauruses: ThesaurusId[],
  state: ThesaurusesState,
): ThesaurusId[] => {
  return thesauruses.filter(
    name => (state[name]?.status ?? 'NotLoaded') === 'NotLoaded',
  );
};

/**
 * Will only render children after thesauruses are successfully loaded.
 * Shows spinner while loading, and an error message if any fails
 */
export const ThesaurusesGate: React.FC<PropsWithChildren<Props>> = ({
  thesauruses,
  children,
}) => {
  const loadThesaurus = useLoadThesaurus();
  const {locale, t} = useLocalization();
  const thesaurusesState = useAppSelector(selectThesaurus);
  const status = useMemo(
    () => getAggregateStatus(thesauruses ?? [], thesaurusesState),
    [thesauruses, thesaurusesState],
  );

  // Trigger loading of thesauruses not yet loaded
  useEffect(() => {
    if (thesauruses !== undefined) {
      const notLoaded = getNotLoaded(thesauruses, thesaurusesState);
      notLoaded.forEach(id => {
        loadThesaurus(id, locale ?? MasterLocale);
      });
    }
    // On thesauruses or local only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thesauruses, locale]);

  return status === 'NotLoaded' ||
    status === 'Loading' ||
    status === 'Failed' ? (
    <Scene>
      <Scene.Header title="" />
      <Scene.Content
        loadStatus={status}
        error={
          status === 'Failed' ? t('thesaurus.load.failed.label') : undefined
        }
        loadingType={t('general.entity.thesaurus')}
      />
    </Scene>
  ) : (
    <>{children}</>
  );
};
